import * as Sentry from '@sentry/electron/renderer'
import { GlobalEvents } from '@highlight/ipc'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getChatApiRoute } from '@renderer/chat/api/v1Client'
import { useAuth } from '@renderer/chat/providers'
import { FOR_YOU_CHATS_QUERY_KEY, HISTORY_ACTIVITY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { REMINDERS_QUERY_KEY, type ReminderApiItem } from '@renderer/overlay/Sidepanel/hooks/use-reminders'
import { STAGED_ACTIONS_QUERY_KEY, type StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { forgetPreviewAcknowledgement } from './conversation-preview-acknowledgements'
import {
  applyConversationPreviewAcknowledgement,
  type ForYouChatsCacheData,
  type ConversationPreviewDisposition,
} from './conversation-preview-cache'
import type { SidepanelChat } from './running-chat-selection'

export type { ConversationPreviewDisposition } from './conversation-preview-cache'

interface AcknowledgeVariables {
  chat: SidepanelChat
  disposition: ConversationPreviewDisposition
}

interface MutationContext {
  previousForYou?: ForYouChatsCacheData
  previousActions?: StagedAction[]
  previousReminders?: ReminderApiItem[]
}

export function useConversationPreviewAcknowledgement() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation<unknown, Error, AcknowledgeVariables, MutationContext>({
    scope: { id: 'conversation-preview-acknowledgement' },
    mutationFn: async ({ chat, disposition }) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/conversations/${chat.id}/preview/acknowledge`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          revision: chat.previewRevision,
          disposition,
          stagedActionIds: chat.stagedActionIds,
          reminderIds: chat.reminderIds,
        }),
      })
      if (!response.ok) {
        throw new Error(`Failed to acknowledge conversation preview: ${response.status}`)
      }
      return response.json()
    },
    onMutate: async ({ chat, disposition }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [REMINDERS_QUERY_KEY] }),
      ])

      const context: MutationContext = {
        previousForYou: queryClient.getQueryData([FOR_YOU_CHATS_QUERY_KEY]),
        previousActions: queryClient.getQueryData([STAGED_ACTIONS_QUERY_KEY]),
        previousReminders: queryClient.getQueryData([REMINDERS_QUERY_KEY]),
      }
      applyConversationPreviewAcknowledgement(queryClient, chat, disposition)
      GlobalEvents.emitToPeers('conversation-preview:acknowledged', chat, disposition)

      return context
    },
    onError: (_error, { chat }, context) => {
      const shouldRestore = forgetPreviewAcknowledgement(chat.id, chat.previewRevision)
      GlobalEvents.emitToPeers('conversation-preview:acknowledgement-reverted', chat.id, chat.previewRevision)
      if (!shouldRestore || !context) {
        return
      }
      // History has no snapshot to roll back to, so a refetch is what restores the row's dot.
      void queryClient
        .invalidateQueries({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] })
        .catch((error) => Sentry.captureException(error))
      if (context.previousForYou !== undefined) {
        queryClient.setQueryData([FOR_YOU_CHATS_QUERY_KEY], context.previousForYou)
      } else {
        queryClient.removeQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY], exact: true })
      }
      if (context.previousActions !== undefined) {
        queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], context.previousActions)
      } else {
        queryClient.removeQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY], exact: true })
      }
      if (context.previousReminders !== undefined) {
        queryClient.setQueryData([REMINDERS_QUERY_KEY], context.previousReminders)
      } else {
        queryClient.removeQueries({ queryKey: [REMINDERS_QUERY_KEY], exact: true })
      }
    },
    onSettled: () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [REMINDERS_QUERY_KEY] }),
      ]).catch((error) => Sentry.captureException(error))
    },
  })

  return { acknowledgePreview: mutation.mutateAsync, isAcknowledging: mutation.isPending }
}
